// useMouseTrail.ts
import { useEffect, useRef } from 'react';
import { useGSAP } from '@/hooks/useGSAP';

interface UseMouseFollowerOptions {
  trailRef: React.RefObject<SVGSVGElement>;
  circleRef: React.RefObject<HTMLDivElement>;
}

const svgns = 'http://www.w3.org/2000/svg';
const ease = 0.8;
const totalPoints = 80;

export const useMouseTrail = ({
  trailRef,
  circleRef,
}: UseMouseFollowerOptions) => {
  const pointerRef = useRef<{ x: number; y: number }>({
    x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0,
    y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0,
  });

  const { gsap, ready } = useGSAP();

  useEffect(() => {
    if (!ready || !gsap || !trailRef.current) return;

    const root = trailRef.current;
    const lines: SVGLineElement[] = [];

    // Position du leader qui sera le premier de la ligne
    let leader = (prop: 'x' | 'y') => pointerRef.current[prop];

    // Crée toutes les lignes, la position de chaque ligne devient le leader de la ligne suivante
    for (let i = 0; i < totalPoints; i++) {
      leader = createLine(leader, i);
    }

    function createLine(leaderFn: (prop: 'x' | 'y') => number, i: number) {
      const line = document.createElementNS(svgns, 'line');
      root.appendChild(line);
      lines.push(line);

      gsap!.set(line, {
        x: leaderFn('x'),
        y: leaderFn('y'),
        opacity: ((totalPoints - i) / totalPoints) * 0.5,
      });

      const pos = gsap!.getProperty(line);
      const posTyped = (prop: 'x' | 'y') => Number(pos(prop));

      // Anime la ligne pour qu'elle suive le leader
      gsap!.to(line, {
        duration: 1000,
        x: '+=1',
        y: '+=1',
        repeat: -1,
        ease: 'none',
        modifiers: {
          x: () => {
            const posX = Number(pos('x'));
            const leaderX = leaderFn('x');
            const x = posX + (leaderX - posX) * ease;
            line.setAttribute('x2', String(leaderX - x));
            return x;
          },
          y: () => {
            const posY = Number(pos('y'));
            const leaderY = leaderFn('y');
            const y = posY + (leaderY - posY) * ease;
            line.setAttribute('y2', String(leaderY - y));
            return y;
          },
        },
      });

      return posTyped;
    }

    const handleMouseMove = (event: MouseEvent) => {
      // Met à jour la position du trail
      pointerRef.current.x = event.clientX;
      pointerRef.current.y = event.clientY;

      // Met à jour la position du cercle
      if (circleRef.current) {
        gsap!.set(circleRef.current, {
          x: event.clientX,
          y: event.clientY,
          xPercent: -50,
          yPercent: -50,
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      lines.forEach((line) => {
        gsap.killTweensOf(line);
        root.removeChild(line);
      });
    };
  }, [trailRef, circleRef, ready, gsap]);
};
